import { useCallback, useContext, useEffect, useState } from "react";
import { SignFormContext } from "../../SignForm";
import api from "../../../../../../service/api";
import useStudentResource from "../../../../BcaAttendance/hooks/useStudentResource";
import handleSignatureSubmit from "./handleSignatureSubmit";

export default function useSignFormPayload() {
    const {
        batchData,
        studentDataArray,
        studentDropdownData,
        pdfTemplateDropdownData,
        pdfUrl,
        setPdfUrl,
        batch_student_id,
        setBatchStudentId,
        template_id,
        setTemplateId,
        payload,
        student_id,
        setStudentId,
        s3Link,
        setS3Link,
        requestRefresh,
        setRequestRefresh
    } = useContext(SignFormContext) ?? {};

    const [mergedPdfUrl, setMergedPdfUrl] = useState(null);
    const [signedStudentIds, setSignedStudentIds] = useState([]);
    const [loadingMergedPdf, setLoadingMergedPdf] = useState(false);
    const [mergedPdfError, setMergedPdfError] = useState(null);
    const [loadingSignedDocs, setLoadingSignedDocs] = useState(false);

    const studentResource = useStudentResource({ id: null });
    const { handleSubmit, loadSignatures } =
        handleSignatureSubmit({ id: payload?.batch_student_id ?? null, setRequestRefresh });

    const handleTemplateSelection = useCallback(
        (selection) => {
            const value = !selection
                ? null
                : selection.value != ""
                ? selection.value
                : null;
            setTemplateId(value);
        },
        [setTemplateId]
    );

    const handleStudentSelection = useCallback(
        (selection) => {
            const value = !selection
                ? null
                : selection.value != ""
                ? selection.value
                : null;
            setBatchStudentId(value);
        },
        [setBatchStudentId]
    );

    useEffect(() => {
        if (!batch_student_id || !template_id) {
            setS3Link(null);
        }

        if (batch_student_id) {
            const student_data = studentDataArray.find(
                (value) => value.id == batch_student_id
            );
            setStudentId(student_data?.student?.id ?? null);
        }

        if (template_id && batch_student_id) {
            fetchPdf();
            gets3PdfLink();
        }
    }, [template_id, batch_student_id]);

    // Fetch merged PDF when template changes
    useEffect(() => {
        // Always clear previous merged PDF first
        if (mergedPdfUrl) {
            URL.revokeObjectURL(mergedPdfUrl);
        }
        setMergedPdfUrl(null);
        setSignedStudentIds([]);
        setMergedPdfError(null);

        // Then fetch new one if template is selected
        if (template_id && studentDropdownData?.length) {
            fetchMergedSignedPdfs();
        }
    }, [template_id, studentDropdownData]);

    useEffect(() => {
        if (requestRefresh) {
            fetchPdf();
            gets3PdfLink();
            fetchMergedSignedPdfs();
            setRequestRefresh(false);
        }
    }, [requestRefresh]);

    // Cleanup blob URLs on unmount
    useEffect(() => {
        return () => {
            if (mergedPdfUrl) {
                URL.revokeObjectURL(mergedPdfUrl);
            }
        };
    }, [mergedPdfUrl]);

    const fetchPdf = async () => {
        try {
            const response = await api.getpdf(
                `tms/bca-forms/view-pdf/${payload.template_id}/${payload.batch_student_id}`,
                payload
            );

            const blob = new Blob([response], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            console.log("SINGLE", url)
            setPdfUrl(url);
        } catch (error) {
            console.error("Error fetching PDF:", error);
        }
    };

    const gets3PdfLink = async () => {
        try {
            const response = await api.get(
                `tms/bca-forms/get-s3-file/${payload.template_id}/${payload.batch_student_id}`,
                payload
            );
            setS3Link(response?.data?.full_path ?? null);
        } catch (error) {
            console.error("Error fetching S3 file link", error);
        }
    };

    const fetchMergedSignedPdfs = async () => {
        if (!template_id || !studentDropdownData?.length) {
            return;
        }

        setLoadingMergedPdf(true);
        setLoadingSignedDocs(true);
        setMergedPdfError(null);

        try {
            // Get all student IDs
            const allStudentIds = studentDropdownData.map((s) => s.value);

            const checkResponse = await api.post(
                `tms/bca-forms/check-signed-documents/${template_id}`,
                { batch_student_ids: allStudentIds }
            );

            const studentIdsWithSignedDocs =
                checkResponse.signed_student_ids || [];
            setSignedStudentIds(studentIdsWithSignedDocs);

            setLoadingSignedDocs(false);

            if (studentIdsWithSignedDocs.length === 0) {
                setMergedPdfError(
                    "No signed documents found for this template"
                );
                setLoadingMergedPdf(false);
                return;
            }

            const response = await api.postPdf(
                `tms/bca-forms/merge-signed-pdfs/${template_id}`,
                {
                    batch_student_ids: studentIdsWithSignedDocs,
                }
            );

            if (mergedPdfUrl) {
                URL.revokeObjectURL(mergedPdfUrl);
            }

            const blob = new Blob([response], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            console.log("MERGE:", url);
            setMergedPdfUrl(url);
        } catch (error) {
            console.error("Error merging PDFs:", error);
            setMergedPdfError("Failed to merge signed PDFs");
            setLoadingSignedDocs(false);
        } finally {
            setLoadingMergedPdf(false);
        }
    };

    return {
        batchData,
        studentDataArray,
        pdfTemplateDropdownData,
        studentDropdownData,
        handleTemplateSelection,
        handleStudentSelection,
        ...studentResource,
        handleSubmit,
        loadSignatures,
        pdfUrl,
        batch_student_id,
        setBatchStudentId,
        template_id,
        setTemplateId,
        payload,
        student_id,
        s3Link,
        setS3Link,
        // Merged PDF functionality
        mergedPdfUrl,
        signedStudentIds,
        loadingMergedPdf,
        mergedPdfError,
        fetchMergedSignedPdfs,
        loadingSignedDocs,
    };
}
